import { ChevronRightIcon, XIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import {
  forwardRef,
  type ReactNode,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import clsxm from '@/lib/clsxm';
import { useFavoriteTools } from '@/hooks/useFavoriteTools';

import { useUser } from '@/utils/useUser';

import { AccountDropdown, AccountPanel, SettingsLink } from './AccountMenu';
import { CreditsCard } from './Credits';
import {
  activeHref,
  APP_ITEMS,
  type NavItem,
  pinnedItems,
  PRIMARY_ITEMS,
  TOOLS_ITEM,
} from './navModel';
import { nav, NavItemBadge, PinButton, useNavTrack } from './navUi';
import PinIcon from './PinIcon';
import WhatsNewBell from './WhatsNewBell';
import ToolsFlyout, { useToolsFlyout } from '../ToolsFlyout';

type PanelVariant = 'sidebar' | 'drawer';

/**
 * The full navigation: the 240 px desktop sidebar of DashboardLayout and,
 * with `variant='drawer'`, the phone menu of both layouts. Home, Tools and
 * History first (History was the most revisited page, buried at the bottom),
 * then the user's pins, then credits and the account button. While the
 * session loads it renders the signed-in frame, since these pages are for
 * signed-in users; visitors get the apps list and Log in / Sign up.
 */
export default function SidebarPanel({
  variant,
  onClose,
}: {
  variant: PanelVariant;
  /** The drawer's close button and link taps. */
  onClose?: () => void;
}) {
  const { t } = useLocale('dashboard');
  const { asPath } = useRouter();
  const track = useNavTrack(variant);
  const { user, isLoading } = useUser();
  const signedIn = !!user || isLoading;
  const {
    favoriteTools,
    isLoading: favoritesLoading,
    toggleFavorite,
  } = useFavoriteTools();
  const drawer = variant === 'drawer';
  // The drawer's footer stays in view while the list scrolls, except with
  // the account section open: a sticky block taller than the screen could
  // never be scrolled to its top.
  const [accountOpen, setAccountOpen] = useState(false);

  const pins = useMemo(
    () => (user ? pinnedItems(favoriteTools) : []),
    [user, favoriteTools],
  );
  const primary = signedIn ? PRIMARY_ITEMS : [TOOLS_ITEM];
  const listed = signedIn ? pins : APP_ITEMS;
  const current = activeHref(
    asPath,
    [...primary, ...listed].map((item) => item.href),
  );

  const go = (section: string, href: string) => {
    track(section, href);
    onClose?.();
  };

  const row = (item: NavItem, section: string, extra?: ReactNode) => (
    <NavRow
      key={item.slug}
      item={item}
      active={current === item.href}
      drawer={drawer}
      onClick={() => go(section, item.href)}
      extra={extra}
    />
  );

  return (
    <div
      data-nav-panel=''
      className={clsxm(
        'flex h-full flex-col',
        nav.panel,
        drawer ? 'w-full' : clsxm('w-60 border-e', nav.hairline),
      )}
    >
      <div
        className={clsxm(
          'flex shrink-0 items-center justify-between gap-2',
          drawer ? 'h-16 pe-2 ps-4' : 'h-14 pe-2 ps-4',
        )}
      >
        <Link
          href={user ? '/dashboard' : '/'}
          onClick={() => go('logo', user ? '/dashboard' : '/')}
          className={clsxm(
            '-mx-1 flex shrink-0 rounded-md px-1 py-1',
            nav.focus,
          )}
        >
          <Image
            src='/images/marky.png'
            width={167}
            height={24}
            alt='Easy-Peasy.AI'
            className='h-6 w-auto'
          />
        </Link>
        <div className='flex items-center gap-0.5'>
          {signedIn && <WhatsNewBell variant={drawer ? 'drawer' : 'sidebar'} />}
          {drawer && (
            <button
              type='button'
              onClick={onClose}
              className={clsxm(
                'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                nav.quiet,
                nav.hover,
                nav.focus,
              )}
            >
              <XIcon className='h-5 w-5' aria-hidden='true' />
              <span className='sr-only'>{t('Close menu')}</span>
            </button>
          )}
        </div>
      </div>

      <Scroller drawer={drawer}>
        <nav
          aria-label={t('Menu')}
          className={clsxm(
            'px-3',
            drawer
              ? 'flex-none pb-4'
              : 'flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain pb-3',
          )}
        >
          <ul className='space-y-0.5'>
            {primary.map((item) =>
              item === TOOLS_ITEM && !drawer ? (
                <ToolsRow
                  key={item.slug}
                  item={item}
                  active={current === item.href}
                  onClick={() => go('primary', item.href)}
                />
              ) : (
                row(item, 'primary')
              ),
            )}
          </ul>

          {signedIn ? (
            <Section label={t('Pinned')} drawer={drawer}>
              {!user || favoritesLoading ? (
                <PinSkeleton drawer={drawer} />
              ) : pins.length ? (
                <ul className='space-y-0.5'>
                  {pins.map((item) =>
                    row(
                      item,
                      'pinned',
                      !drawer && (
                        <PinButton
                          pinned
                          onToggle={() => {
                            track('pin', item.slug, { pinned: false });
                            toggleFavorite(item.slug);
                          }}
                        />
                      ),
                    ),
                  )}
                </ul>
              ) : (
                <p
                  className={clsxm(
                    'flex items-start gap-2 px-2.5 py-1.5 leading-5',
                    drawer ? 'text-[14px]' : 'text-[12px]',
                    nav.quiet,
                  )}
                >
                  <PinIcon className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                  {t('Pin a tool and it shows up here.')}
                </p>
              )}
            </Section>
          ) : (
            <Section label={t('Apps')} drawer={drawer}>
              <ul className='space-y-0.5'>
                {APP_ITEMS.map((item) => row(item, 'apps'))}
              </ul>
            </Section>
          )}

          {/* In the list's box, docked to its bottom, not in the footer:
              the meter appears once the plan is known, and in the footer it
              would move the account button. */}
          {!drawer && user && (
            <CreditsCard
              variant='sidebar'
              className={clsxm('sticky bottom-0 mt-auto pt-3', nav.panel)}
            />
          )}
        </nav>

        <div
          className={clsxm(
            'space-y-2 border-t px-3',
            nav.hairline,
            drawer
              ? clsxm(
                  'mt-auto pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3',
                  nav.panel,
                  !accountOpen && 'sticky bottom-0',
                )
              : 'shrink-0 py-3',
          )}
        >
          {user ? (
            <>
              {drawer && <CreditsCard variant='drawer' />}
              {drawer ? (
                <AccountPanel
                  open={accountOpen}
                  onOpenChange={setAccountOpen}
                />
              ) : (
                <div className='flex items-center gap-1'>
                  <AccountDropdown variant='sidebar' />
                  <SettingsLink variant='sidebar' />
                </div>
              )}
            </>
          ) : isLoading ? (
            <div
              aria-hidden='true'
              className='flex h-11 items-center gap-2.5 px-2'
            >
              <span className='h-7 w-7 animate-pulse rounded-full bg-[#0f0f28]/[0.07] dark:bg-white/10' />
              <span className='h-3 w-24 animate-pulse rounded-full bg-[#0f0f28]/[0.07] dark:bg-white/10' />
            </div>
          ) : (
            <div className='flex gap-2'>
              <Link
                href='/login'
                onClick={() => go('auth', '/login')}
                className={clsxm(
                  'flex flex-1 items-center justify-center rounded-xl text-[13px] font-semibold ring-1 ring-[#0f0f28]/[0.1] transition hover:bg-[#f4f4fb] dark:ring-white/15 dark:hover:bg-white/[0.06]',
                  drawer ? 'h-11 text-[15px]' : 'h-9',
                  nav.ink,
                  nav.focus,
                )}
              >
                {t('Login')}
              </Link>
              <Link
                href='/signup'
                onClick={() => go('auth', '/signup')}
                className={clsxm(
                  'flex flex-1 items-center justify-center rounded-xl bg-primary-600 text-[13px] font-semibold text-white transition-colors hover:bg-primary-700',
                  drawer ? 'h-11 text-[15px]' : 'h-9',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
                )}
              >
                {t('Sign up')}
              </Link>
            </div>
          )}
        </div>
      </Scroller>
    </div>
  );
}

/** The drawer scrolls as one column; the sidebar scrolls its list only. */
function Scroller({
  drawer,
  children,
}: {
  drawer: boolean;
  children: ReactNode;
}) {
  if (!drawer) return <>{children}</>;
  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain'>
      {children}
    </div>
  );
}

function Section({
  label,
  drawer,
  children,
}: {
  label: string;
  drawer: boolean;
  children: ReactNode;
}) {
  return (
    <div className='mt-5'>
      <h2
        className={clsxm(
          'px-2.5 pb-1 font-medium',
          drawer ? 'text-[13px]' : 'text-[12px]',
          nav.quiet,
        )}
      >
        {label}
      </h2>
      {children}
    </div>
  );
}

function PinSkeleton({ drawer }: { drawer: boolean }) {
  return (
    <div aria-hidden='true' className='space-y-0.5'>
      {[64, 48, 72].map((width) => (
        <div
          key={width}
          className={clsxm(
            'flex items-center gap-2.5 px-2.5',
            drawer ? 'h-11' : 'h-8',
          )}
        >
          <span className='h-[18px] w-[18px] animate-pulse rounded-md bg-[#0f0f28]/[0.06] dark:bg-white/10' />
          <span
            style={{ width: `${width}%` }}
            className='h-2.5 animate-pulse rounded-full bg-[#0f0f28]/[0.06] dark:bg-white/10'
          />
        </div>
      ))}
    </div>
  );
}

/** One link, with room for a trailing button (pin star, flyout chevron). */
const NavRow = forwardRef<
  HTMLLIElement,
  {
    item: NavItem;
    active: boolean;
    drawer: boolean;
    onClick: () => void;
    extra?: ReactNode;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
  }
>(function NavRow(
  { item, active, drawer, onClick, extra, onMouseEnter, onMouseLeave },
  ref,
) {
  const { t } = useLocale('dashboard');
  const label = item.translate === false ? item.name : t(item.name);
  return (
    <li
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={clsxm(
        'group/row flex items-center rounded-lg transition-colors',
        active ? nav.active : 'hover:bg-[#f4f4fb] dark:hover:bg-white/[0.06]',
      )}
    >
      <Link
        href={item.href}
        aria-current={active ? 'page' : undefined}
        title={label.length > 24 ? label : undefined}
        onClick={onClick}
        className={clsxm(
          'flex min-w-0 flex-1 items-center rounded-lg font-medium',
          drawer
            ? 'h-11 gap-3 px-3 text-[15px]'
            : 'h-8 gap-2.5 px-2.5 text-[13px]',
          !active &&
            clsxm(
              nav.body,
              'group-hover/row:text-[#10101c] dark:group-hover/row:text-white',
            ),
          nav.focus,
        )}
      >
        <item.icon
          aria-hidden='true'
          className={clsxm(
            'shrink-0',
            drawer ? 'h-5 w-5' : 'h-[18px] w-[18px]',
            active
              ? nav.iconActive
              : 'text-[#8b8ba0] group-hover/row:text-[#56566d] dark:text-slate-500 dark:group-hover/row:text-slate-300',
          )}
        />
        <span className='truncate'>{label}</span>
        {item.badge && <NavItemBadge kind={item.badge} />}
      </Link>
      {extra}
    </li>
  );
});

/**
 * The Tools row opens the flyout on hover, and from its chevron by click or
 * keyboard (it used to be mouse-only).
 */
function ToolsRow({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
}) {
  const { t } = useLocale('dashboard');
  const flyout = useToolsFlyout();
  const anchorRef = useRef<HTMLLIElement>(null);
  const chevronRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  return (
    <NavRow
      ref={anchorRef}
      item={item}
      active={active}
      drawer={false}
      onClick={onClick}
      onMouseEnter={() =>
        anchorRef.current && flyout.hoverIn(anchorRef.current)
      }
      onMouseLeave={flyout.hoverOut}
      extra={
        <>
          <button
            ref={chevronRef}
            type='button'
            aria-expanded={flyout.open}
            aria-controls={flyout.open ? id : undefined}
            aria-label={t('Browse tools')}
            onClick={() =>
              anchorRef.current && flyout.toggle(anchorRef.current)
            }
            className={clsxm(
              'me-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition',
              'text-[#8b8ba0] hover:bg-[#0f0f28]/[0.06] hover:text-[#10101c] dark:text-slate-500 dark:hover:bg-white/10 dark:hover:text-white',
              flyout.open && 'text-[#10101c] dark:text-white',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600',
            )}
          >
            <ChevronRightIcon
              aria-hidden='true'
              className='h-3.5 w-3.5 rtl:rotate-180'
            />
          </button>
          {flyout.open && flyout.anchor && (
            <ToolsFlyout
              id={id}
              anchor={flyout.anchor}
              variant='sidebar'
              focusOnOpen={flyout.focusOnOpen}
              onMouseEnter={flyout.keep}
              onMouseLeave={flyout.hoverOut}
              onClose={(returnFocus) => {
                flyout.close();
                if (returnFocus) chevronRef.current?.focus();
              }}
            />
          )}
        </>
      }
    />
  );
}
